<template>
  <div>
    <b-card>
      <!-- Header -->
      <b-row class="mb-3">
        <b-col>
          <h3 class="mb-0">Course Management</h3>
          <p class="text-muted">Manage and moderate courses</p>
        </b-col>
        <b-col class="text-right">
          <b-button variant="primary" @click="refreshCourses">
            <feather type="refresh-cw" class="feather-sm"></feather>
            Refresh
          </b-button>
        </b-col>
      </b-row>

      <!-- Filters -->
      <b-row class="mb-3">
        <b-col md="3">
          <b-form-select v-model="filter.status" @change="filterCourses">
            <b-form-select-option :value="null">All Status</b-form-select-option>
            <b-form-select-option value="pending">Pending Approval</b-form-select-option>
            <b-form-select-option value="approved">Approved</b-form-select-option>
            <b-form-select-option value="rejected">Rejected</b-form-select-option>
            <b-form-select-option value="published">Published</b-form-select-option>
          </b-form-select>
        </b-col>
        <b-col md="6">
          <b-form-input
            v-model="filter.search"
            placeholder="Search by course name or teacher"
            @input="searchCourses"
          ></b-form-input>
        </b-col>
      </b-row>

      <!-- Stats Cards -->
      <b-row class="mb-4">
        <b-col md="3">
          <b-card bg-variant="primary" text-variant="white">
            <h3 class="mb-0">{{ stats.totalCourses || 0 }}</h3>
            <p class="mb-0">Total Courses</p>
          </b-card>
        </b-col>
        <b-col md="3">
          <b-card bg-variant="warning" text-variant="white">
            <h3 class="mb-0">{{ stats.pendingApproval || 0 }}</h3>
            <p class="mb-0">Pending Approval</p>
          </b-card>
        </b-col>
        <b-col md="3">
          <b-card bg-variant="success" text-variant="white">
            <h3 class="mb-0">{{ stats.approved || 0 }}</h3>
            <p class="mb-0">Approved</p>
          </b-card>
        </b-col>
        <b-col md="3">
          <b-card bg-variant="info" text-variant="white">
            <h3 class="mb-0">{{ stats.published || 0 }}</h3>
            <p class="mb-0">Published</p>
          </b-card>
        </b-col>
      </b-row>

      <!-- Courses Table -->
      <b-table
        :items="filteredCourses"
        :fields="fields"
        :busy="loading"
        :per-page="perPage"
        :current-page="currentPage"
        striped
        hover
        responsive
        show-empty
      >
        <template #table-busy>
          <div class="text-center my-5">
            <b-spinner class="align-middle mr-2"></b-spinner>
            <strong>Loading courses...</strong>
          </div>
        </template>

        <template #cell(title)="data">
          <div>
            <div class="font-weight-bold">{{ data.value }}</div>
            <small class="text-muted">{{ truncate(data.item.description, 60) }}</small>
          </div>
        </template>

        <template #cell(teacher)="data">
          {{ data.item.teacher_name || 'N/A' }}
        </template>

        <template #cell(fee)="data">
          PKR {{ Number(data.value || 0).toLocaleString() }}
        </template>

        <template #cell(capacity)="data">
          <b-badge :variant="data.item.enrollments >= data.value ? 'danger' : 'success'">
            {{ data.item.enrollments || 0 }} / {{ data.value }}
          </b-badge>
        </template>

        <template #cell(rating)="data">
          <div>
            <b-icon icon="star-fill" variant="warning"></b-icon>
            {{ data.value ? data.value.toFixed(1) : 'N/A' }}
          </div>
        </template>

        <template #cell(status)="data">
          <b-badge :variant="getStatusVariant(data.item.displayStatus)">
            {{ data.item.displayStatus || 'Pending' }}
          </b-badge>
        </template>

        <template #cell(created_at)="data">
          {{ formatDate(data.value) }}
        </template>

        <template #cell(actions)="data">
          <b-dropdown variant="link" no-caret right size="sm">
            <template #button-content>
              <feather type="more-vertical" class="feather-sm"></feather>
            </template>
            <b-dropdown-item @click="viewCourse(data.item)">
              <feather type="eye" class="feather-sm mr-2"></feather>
              View Details
            </b-dropdown-item>
            <b-dropdown-item @click="openEditCourse(data.item)">
              <feather type="edit-2" class="feather-sm mr-2"></feather>
              Edit Course
            </b-dropdown-item>
            <b-dropdown-divider></b-dropdown-divider>
            <b-dropdown-item
              v-if="data.item.status === 1"
              @click="approveCourse(data.item)"
              variant="success"
            >
              <feather type="check-circle" class="feather-sm mr-2"></feather>
              Approve Course
            </b-dropdown-item>
            <b-dropdown-item
              v-if="data.item.status === 1"
              @click="rejectCourse(data.item)"
              variant="danger"
            >
              <feather type="x-circle" class="feather-sm mr-2"></feather>
              Reject Course
            </b-dropdown-item>
            <b-dropdown-item @click="deleteCourse(data.item)" variant="danger">
              <feather type="trash-2" class="feather-sm mr-2"></feather>
              Delete Course
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>

      <!-- Pagination -->
      <b-row>
        <b-col md="6">
          <span class="text-muted">
            Showing {{ (currentPage - 1) * perPage + 1 }} to
            {{ Math.min(currentPage * perPage, filteredCourses.length) }} of
            {{ filteredCourses.length }} courses
          </span>
        </b-col>
        <b-col md="6">
          <b-pagination
            v-model="currentPage"
            :total-rows="filteredCourses.length"
            :per-page="perPage"
            align="right"
            size="sm"
          ></b-pagination>
        </b-col>
      </b-row>
    </b-card>

    <!-- Course Detail Modal -->
    <b-modal
      v-model="showCourseModal"
      size="xl"
      title="Course Details"
      hide-footer
      scrollable
    >
      <div v-if="selectedCourse">
        <b-row>
          <b-col md="8">
            <h4>{{ selectedCourse.title }}</h4>
            <p class="text-muted">{{ selectedCourse.description }}</p>
          </b-col>
          <b-col md="4" class="text-right">
            <h3 class="text-primary">PKR {{ Number(selectedCourse.fee || 0).toLocaleString() }}</h3>
            <b-badge :variant="getStatusVariant(selectedCourse.displayStatus)" class="p-2">
              {{ selectedCourse.displayStatus }}
            </b-badge>
          </b-col>
        </b-row>

        <hr />

        <!-- Basic Info -->
        <h5 class="mb-3">Basic Information</h5>
        <b-row>
          <b-col md="6">
            <strong>Course ID:</strong> {{ selectedCourse.course_id }}
          </b-col>
          <b-col md="6">
            <strong>Teacher:</strong> {{ selectedCourse.teacher_name || selectedCourse.teacher || 'N/A' }}
          </b-col>
          <b-col md="6" class="mt-2">
            <strong>Teacher Email:</strong> {{ selectedCourse.teacher_email || 'N/A' }}
          </b-col>
          <b-col md="6" class="mt-2">
            <strong>Category ID:</strong> {{ selectedCourse.category_id || 'N/A' }}
          </b-col>
          <b-col md="6" class="mt-2">
            <strong>Level ID:</strong> {{ selectedCourse.level_id || 'N/A' }}
          </b-col>
          <b-col md="6" class="mt-2">
            <strong>Rating:</strong>
            <b-icon icon="star-fill" variant="warning"></b-icon>
            {{ selectedCourse.rating || 0 }}/5
          </b-col>
          <b-col md="6" class="mt-2">
            <strong>Current Chapter:</strong> {{ selectedCourse.chapter || 'N/A' }}
          </b-col>
          <b-col md="6" class="mt-2">
            <strong>Live Status:</strong> 
            <b-badge :variant="selectedCourse.is_live ? 'success' : 'secondary'">
              {{ selectedCourse.is_live ? 'Live' : 'Not Live' }}
            </b-badge>
          </b-col>
          <b-col md="12" class="mt-2" v-if="selectedCourse.course_link">
            <strong>Course Link:</strong> 
            <a :href="selectedCourse.course_link" target="_blank">{{ selectedCourse.course_link }}</a>
          </b-col>
        </b-row>

        <hr />

        <!-- Schedule -->
        <h5 class="mb-3">Schedule & Dates</h5>
        <b-row>
          <b-col md="6">
            <strong>Start Date:</strong> {{ formatDate(selectedCourse.start_date) }}
          </b-col>
          <b-col md="6">
            <strong>End Date:</strong> {{ formatDate(selectedCourse.end_date) }}
          </b-col>
          <b-col md="12" class="mt-2" v-if="parsedSchedule">
            <strong>Weekdays:</strong> {{ getWeekdaysText(parsedSchedule.weekdays) }}
          </b-col>
          <b-col md="12" class="mt-2" v-if="parsedSchedule && parsedSchedule.timings">
            <strong>Time:</strong> {{ formatTime(parsedSchedule.timings[0]) }} - {{ formatTime(parsedSchedule.timings[1]) }}
          </b-col>
          <b-col md="6" class="mt-2">
            <strong>Created On:</strong> {{ formatDate(selectedCourse.created_on) }}
          </b-col>
        </b-row>

        <hr />

        <!-- Configuration -->
        <h5 class="mb-3">Course Configuration</h5>
        <b-row v-if="parsedConfig">
          <b-col md="6">
            <strong>Max Capacity:</strong> {{ parsedConfig.max || 'N/A' }}
          </b-col>
          <b-col md="6">
            <strong>Registration Fee:</strong> PKR {{ parsedConfig.fee || 0 }}
          </b-col>
          <b-col md="6" class="mt-2">
            <strong>Join After Start:</strong> 
            <b-badge :variant="parsedConfig.joining_After_start ? 'success' : 'danger'">
              {{ parsedConfig.joining_After_start ? 'Yes' : 'No' }}
            </b-badge>
          </b-col>
          <b-col md="6" class="mt-2">
            <strong>Pay Before Joining:</strong> 
            <b-badge :variant="parsedConfig.joining_before_fee ? 'success' : 'danger'">
              {{ parsedConfig.joining_before_fee ? 'Yes' : 'No' }}
            </b-badge>
          </b-col>
        </b-row>

        <hr />

        <!-- Course Contents/Chapters -->
        <h5 class="mb-3">Course Contents</h5>
        <div v-if="parsedContents && parsedContents.length > 0">
          <b-list-group>
            <b-list-group-item v-for="(content, index) in parsedContents" :key="index">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <strong>{{ content.title || 'Chapter ' + (index + 1) }}</strong>
                  <p class="mb-0 text-muted small">{{ content.description || 'No description' }}</p>
                </div>
                <b-badge :variant="content.completed ? 'success' : 'warning'">
                  {{ content.completed ? 'Completed' : 'Pending' }}
                </b-badge>
              </div>
            </b-list-group-item>
          </b-list-group>
        </div>
        <p v-else class="text-muted">No course contents available</p>

        <hr />

        <!-- Enrolled Students -->
        <h5 class="mb-3">Enrolled Students ({{ enrolledStudents.length }})</h5>
        <b-list-group class="mb-3">
          <b-list-group-item
            v-for="student in enrolledStudents"
            :key="student.user_id"
            class="d-flex justify-content-between align-items-center"
          >
            <div>
              <div class="font-weight-bold">{{ student.name }}</div>
              <small class="text-muted">{{ student.email }} &middot; Enrolled {{ formatDate(student.reg_date) }}</small>
            </div>
            <b-button
              size="sm"
              variant="outline-danger"
              :disabled="removingUserId === student.user_id"
              @click="removeStudent(student)"
            >
              <feather type="user-minus" class="feather-sm mr-1"></feather>
              Remove
            </b-button>
          </b-list-group-item>
          <b-list-group-item v-if="!loadingRegistrations && enrolledStudents.length === 0" class="text-muted">
            No students enrolled yet.
          </b-list-group-item>
        </b-list-group>

        <b-row class="mb-4 align-items-end">
          <b-col md="8">
            <label class="mb-1 small text-muted">Enroll a Student</label>
            <b-form-select v-model="studentToEnroll" :options="availableStudentOptions"></b-form-select>
          </b-col>
          <b-col md="4">
            <b-button variant="primary" block :disabled="!studentToEnroll || enrolling" @click="enrollStudent">
              <feather type="user-plus" class="feather-sm mr-1"></feather>
              Enroll
            </b-button>
          </b-col>
        </b-row>

        <hr />

        <!-- Post a message -->
        <h5 class="mb-3">Post a Message to This Course</h5>
        <b-form-textarea
          v-model="messageText"
          rows="2"
          placeholder="Write an announcement for the teacher and enrolled students..."
          class="mb-2"
        ></b-form-textarea>
        <div class="text-right mb-3">
          <b-button variant="secondary" :disabled="!messageText || sendingMessage" @click="sendCourseMessage">
            <feather type="send" class="feather-sm mr-1"></feather>
            Send Message
          </b-button>
        </div>

        <hr />

        <!-- Actions -->
        <div class="mt-4 text-right">
          <b-button
            v-if="selectedCourse.status === 1"
            variant="success"
            @click="approveCourse(selectedCourse)"
            class="mr-2"
          >
            <feather type="check-circle" class="feather-sm mr-2"></feather>
            Approve Course
          </b-button>
          <b-button
            v-if="selectedCourse.status === 1"
            variant="danger"
            @click="rejectCourse(selectedCourse)"
            class="mr-2"
          >
            <feather type="x-circle" class="feather-sm mr-2"></feather>
            Reject Course
          </b-button>
          <b-button variant="outline-primary" @click="openEditCourse(selectedCourse)" class="mr-2">
            <feather type="edit-2" class="feather-sm mr-2"></feather>
            Edit Course
          </b-button>
          <b-button variant="secondary" @click="showCourseModal = false">
            Close
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- Edit Course Modal -->
    <b-modal
      v-model="showEditModal"
      size="xl"
      title="Edit Course"
      scrollable
      @ok.prevent="saveCourseEdits"
      :ok-disabled="savingCourse"
      :ok-title="savingCourse ? 'Saving...' : 'Save Changes'"
      ok-variant="primary"
      cancel-title="Cancel"
    >
      <div v-if="editForm">
        <h5 class="mb-3">Basic Information</h5>
        <b-row>
          <b-col md="12">
            <b-form-group label="Title">
              <b-form-input v-model="editForm.title"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group label="Description">
              <b-form-textarea v-model="editForm.description" rows="3"></b-form-textarea>
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group label="Course Link (meeting URL)">
              <b-form-input v-model="editForm.courseLink"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Fee (PKR)">
              <b-form-input type="number" v-model.number="editForm.fee"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Category">
              <b-form-select v-model="editForm.categoryId" :options="categoryOptions"></b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Level">
              <b-form-select v-model="editForm.levelId" :options="levelOptions"></b-form-select>
            </b-form-group>
          </b-col>
        </b-row>

        <hr />

        <h5 class="mb-3">Schedule</h5>
        <b-row>
          <b-col md="6">
            <b-form-group label="Start Date">
              <b-form-input type="date" v-model="editForm.startDate"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="End Date">
              <b-form-input type="date" v-model="editForm.endDate"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group label="Weekdays">
              <b-form-checkbox-group v-model="editForm.weekdayIds" :options="weekdayOptions"></b-form-checkbox-group>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <label class="mb-1">Class Start Time</label>
            <b-row>
              <b-col cols="6">
                <b-form-select v-model.number="editForm.startHour" :options="hourOptions"></b-form-select>
              </b-col>
              <b-col cols="6">
                <b-form-select v-model.number="editForm.startMinute" :options="minuteOptions"></b-form-select>
              </b-col>
            </b-row>
          </b-col>
          <b-col md="6">
            <label class="mb-1">Class End Time</label>
            <b-row>
              <b-col cols="6">
                <b-form-select v-model.number="editForm.endHour" :options="hourOptions"></b-form-select>
              </b-col>
              <b-col cols="6">
                <b-form-select v-model.number="editForm.endMinute" :options="minuteOptions"></b-form-select>
              </b-col>
            </b-row>
          </b-col>
        </b-row>

        <hr />

        <h5 class="mb-3">Configuration</h5>
        <b-row>
          <b-col md="4">
            <b-form-group label="Max Capacity">
              <b-form-input type="number" v-model.number="editForm.max"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="4" class="d-flex align-items-center">
            <b-form-checkbox v-model="editForm.joiningAfterStart" switch class="mt-3">
              Allow joining after start
            </b-form-checkbox>
          </b-col>
          <b-col md="4" class="d-flex align-items-center">
            <b-form-checkbox v-model="editForm.joiningBeforeFee" switch class="mt-3">
              Require payment before joining
            </b-form-checkbox>
          </b-col>
        </b-row>

        <hr />

        <h5 class="mb-3 d-flex justify-content-between align-items-center">
          <span>Course Contents / Chapters</span>
          <b-button size="sm" variant="outline-primary" @click="addChapter">
            <feather type="plus" class="feather-sm mr-1"></feather>
            Add Chapter
          </b-button>
        </h5>
        <div v-if="editForm.contents.length === 0" class="text-muted mb-3">
          No chapters yet -- add one above.
        </div>
        <b-card
          v-for="(chapter, index) in editForm.contents"
          :key="chapter.id"
          class="mb-2"
          bg-variant="light"
        >
          <b-row>
            <b-col md="5">
              <b-form-group label="Chapter Title" label-class="small text-muted mb-1">
                <b-form-input v-model="chapter.title" placeholder="Chapter title"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="5">
              <b-form-group label="Description" label-class="small text-muted mb-1">
                <b-form-input v-model="chapter.description" placeholder="Short description"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="2" class="d-flex flex-column justify-content-center align-items-center">
              <b-form-checkbox v-model="chapter.completed" switch class="mb-2">
                Completed
              </b-form-checkbox>
              <b-button size="sm" variant="outline-danger" @click="removeChapter(index)">
                <feather type="trash-2" class="feather-sm"></feather>
              </b-button>
            </b-col>
          </b-row>
        </b-card>
      </div>
    </b-modal>
  </div>
</template>

<script>
import CourseService from '@/services/course.service';
import RegistrationService from '@/services/registration.service';
import UserService from '@/services/user.service';
import MessageService from '@/services/message.service';
import CategoryService from '@/services/category.service';
import moment from 'moment';

// Matches the weekday id scheme used by the mobile app's schedule picker
// (0 = Monday ... 6 = Sunday) -- must stay in sync, it's read by the
// ClassCalendar / Home "upcoming classes" schedule computation.
const WEEKDAYS = [
  { id: 0, title: 'Monday' },
  { id: 1, title: 'Tuesday' },
  { id: 2, title: 'Wednesday' },
  { id: 3, title: 'Thursday' },
  { id: 4, title: 'Friday' },
  { id: 5, title: 'Saturday' },
  { id: 6, title: 'Sunday' },
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => ({ value: h, text: h.toString().padStart(2, '0') }));
const MINUTE_OPTIONS = [0, 15, 30, 45].map(m => ({ value: m, text: m.toString().padStart(2, '0') }));

const emptyEditForm = () => ({
  courseId: null,
  title: '',
  description: '',
  courseLink: '',
  fee: 0,
  categoryId: null,
  levelId: null,
  startDate: '',
  endDate: '',
  weekdayIds: [],
  startHour: 9,
  startMinute: 0,
  endHour: 10,
  endMinute: 0,
  max: '',
  joiningAfterStart: true,
  joiningBeforeFee: false,
  contents: [],
  originalSchedule: {},
  originalConfig: {},
});

export default {
  name: 'CourseManagement',
  data() {
    return {
      courses: [],
      filteredCourses: [],
      loading: false,
      filter: {
        status: null,
        search: '',
      },
      stats: {
        totalCourses: 0,
        pendingApproval: 0,
        approved: 0,
        published: 0,
      },
      fields: [
        { key: 'title', label: 'Course', sortable: true },
        { key: 'teacher', label: 'Teacher', sortable: true },
        { key: 'fee', label: 'Fee', sortable: true },
        { key: 'capacity', label: 'Enrollments', sortable: true },
        { key: 'rating', label: 'Rating', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'created_at', label: 'Created', sortable: true },
        { key: 'actions', label: 'Actions' },
      ],
      perPage: 10,
      currentPage: 1,
      showCourseModal: false,
      selectedCourse: null,
      allUsers: [],
      courseRegistrations: [],
      loadingRegistrations: false,
      studentToEnroll: null,
      enrolling: false,
      removingUserId: null,
      messageText: '',
      sendingMessage: false,
      categories: [],
      levels: [],
      showEditModal: false,
      savingCourse: false,
      editForm: emptyEditForm(),
    };
  },
  created() {
    this.loadCourses();
    this.loadAllUsers();
    this.loadCategories();
    this.loadLevels();
  },
  computed: {
    categoryOptions() {
      const options = this.categories.map(c => ({ value: c.category_id, text: c.category_title }));
      return [{ value: null, text: 'Select a category...' }, ...options];
    },
    levelOptions() {
      const options = this.levels.map(l => ({ value: l.level_id, text: l.level_title }));
      return [{ value: null, text: 'Select a level...' }, ...options];
    },
    weekdayOptions() {
      return WEEKDAYS.map(w => ({ value: w.id, text: w.title }));
    },
    hourOptions() {
      return HOUR_OPTIONS;
    },
    minuteOptions() {
      return MINUTE_OPTIONS;
    },
    usersById() {
      const map = {};
      this.allUsers.forEach(u => { map[u.user_id] = u; });
      return map;
    },
    enrolledStudents() {
      return this.courseRegistrations.map(reg => {
        const u = this.usersById[reg.user_id];
        const name = u ? `${u.fname || ''} ${u.lname || ''}`.trim() : '';
        return {
          ...reg,
          name: name || (u ? u.email : `User #${reg.user_id}`),
          email: u ? u.email : '',
        };
      });
    },
    availableStudentOptions() {
      const enrolledIds = new Set(this.courseRegistrations.map(r => r.user_id));
      const options = this.allUsers
        .filter(u => u.user_type === 0 && !enrolledIds.has(u.user_id))
        .map(u => ({
          value: u.user_id,
          text: `${u.fname || ''} ${u.lname || ''}`.trim() + (u.email ? ` (${u.email})` : ''),
        }));
      return [{ value: null, text: 'Select a student...' }, ...options];
    },
    parsedSchedule() {
      if (!this.selectedCourse?.schedule) return null;
      try {
        return typeof this.selectedCourse.schedule === 'string' 
          ? JSON.parse(this.selectedCourse.schedule) 
          : this.selectedCourse.schedule;
      } catch (e) {
        console.error('Error parsing schedule:', e);
        return null;
      }
    },
    parsedConfig() {
      if (!this.selectedCourse?.config) return null;
      try {
        return typeof this.selectedCourse.config === 'string' 
          ? JSON.parse(this.selectedCourse.config) 
          : this.selectedCourse.config;
      } catch (e) {
        console.error('Error parsing config:', e);
        return null;
      }
    },
    parsedContents() {
      if (!this.selectedCourse?.contents) return [];
      try {
        const contents = typeof this.selectedCourse.contents === 'string' 
          ? JSON.parse(this.selectedCourse.contents) 
          : this.selectedCourse.contents;
        return Array.isArray(contents) ? contents : [];
      } catch (e) {
        console.error('Error parsing contents:', e);
        return [];
      }
    },
  },
  methods: {
    async loadCourses() {
      this.loading = true;
      try {
        const response = await CourseService.getAllCourses();
        // Map integer status to string for display
        this.courses = response.data.map(course => ({
          ...course,
          displayStatus: this.getStatusLabel(course.status),
        }));
        this.filteredCourses = this.courses;
        this.calculateStats();
      } catch (error) {
        this.$bvToast.toast('Failed to load courses', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      } finally {
        this.loading = false;
      }
    },
    getStatusLabel(status) {
      // Map integer status to display string
      const statusMap = {
        0: 'rejected',
        1: 'pending',
        2: 'approved',
      };
      return statusMap[status] || 'pending';
    },
    calculateStats() {
      this.stats.totalCourses = this.courses.length;
      this.stats.pendingApproval = this.courses.filter(c => c.status === 1).length;
      this.stats.approved = this.courses.filter(c => c.status === 2).length;
      this.stats.published = this.courses.filter(c => c.status === 2).length;
    },
    filterCourses() {
      let result = [...this.courses];

      if (this.filter.status) {
        result = result.filter(c => c.displayStatus === this.filter.status);
      }

      if (this.filter.search) {
        const search = this.filter.search.toLowerCase();
        result = result.filter(
          c =>
            c.title?.toLowerCase().includes(search) ||
            c.teacher_name?.toLowerCase().includes(search) ||
            c.description?.toLowerCase().includes(search)
        );
      }

      this.filteredCourses = result;
      this.currentPage = 1;
    },
    searchCourses() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.filterCourses();
      }, 300);
    },
    refreshCourses() {
      this.loadCourses();
    },
    viewCourse(course) {
      this.selectedCourse = course;
      this.showCourseModal = true;
      this.studentToEnroll = null;
      this.messageText = '';
      this.loadCourseRegistrations(course.course_id);
    },
    async loadAllUsers() {
      try {
        const response = await UserService.getAllUsers();
        this.allUsers = response.data;
      } catch (error) {
        // Non-fatal -- enrolled-student rows just fall back to "User #id"
      }
    },
    async loadCourseRegistrations(courseId) {
      this.loadingRegistrations = true;
      try {
        const response = await RegistrationService.getRegistrationsByCourse(courseId);
        this.courseRegistrations = response.data || [];
      } catch (error) {
        this.courseRegistrations = [];
      } finally {
        this.loadingRegistrations = false;
      }
    },
    async enrollStudent() {
      if (!this.studentToEnroll || !this.selectedCourse) return;
      this.enrolling = true;
      try {
        await RegistrationService.enrollUser(this.studentToEnroll, this.selectedCourse.course_id);
        this.$bvToast.toast('Student enrolled successfully', {
          title: 'Success',
          variant: 'success',
          solid: true,
        });
        this.studentToEnroll = null;
        await this.loadCourseRegistrations(this.selectedCourse.course_id);
      } catch (error) {
        this.$bvToast.toast(error.response?.data?.message || 'Failed to enroll student', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      } finally {
        this.enrolling = false;
      }
    },
    async removeStudent(student) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Remove ${student.name || 'this student'} from "${this.selectedCourse.title}"?`,
        {
          title: 'Confirm Removal',
          okVariant: 'danger',
          okTitle: 'Remove',
          cancelTitle: 'Cancel',
        }
      );
      if (!confirmed) return;

      this.removingUserId = student.user_id;
      try {
        await RegistrationService.removeUser(student.user_id, this.selectedCourse.course_id);
        this.$bvToast.toast('Student removed from course', {
          title: 'Success',
          variant: 'success',
          solid: true,
        });
        await this.loadCourseRegistrations(this.selectedCourse.course_id);
      } catch (error) {
        this.$bvToast.toast(error.response?.data?.message || 'Failed to remove student', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      } finally {
        this.removingUserId = null;
      }
    },
    async sendCourseMessage() {
      if (!this.messageText || !this.selectedCourse) return;
      this.sendingMessage = true;
      try {
        await MessageService.addCourseMessage(this.selectedCourse.course_id, this.messageText);
        this.$bvToast.toast('Message posted to course', {
          title: 'Success',
          variant: 'success',
          solid: true,
        });
        this.messageText = '';
      } catch (error) {
        this.$bvToast.toast(error.response?.data?.message || 'Failed to post message', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      } finally {
        this.sendingMessage = false;
      }
    },
    async loadCategories() {
      try {
        const response = await CategoryService.getAllCategories();
        this.categories = response.data || [];
      } catch (error) {
        this.categories = [];
      }
    },
    async loadLevels() {
      try {
        const response = await CategoryService.getAllLevels();
        this.levels = response.data || [];
      } catch (error) {
        this.levels = [];
      }
    },
    openEditCourse(course) {
      const schedule = this.parseJsonField(course.schedule) || {};
      const config = this.parseJsonField(course.config) || {};
      const contents = this.parseJsonField(course.contents) || [];
      const timings = Array.isArray(schedule.timings) ? schedule.timings : [900, 1000];

      // Category/level lists only include active ones -- if the course points
      // at one that's since been deactivated, keep it selectable so saving
      // doesn't silently wipe it out.
      if (course.category_id && !this.categories.some(c => c.category_id === course.category_id)) {
        this.categories = [...this.categories, { category_id: course.category_id, category_title: `#${course.category_id} (inactive)` }];
      }
      if (course.level_id && !this.levels.some(l => l.level_id === course.level_id)) {
        this.levels = [...this.levels, { level_id: course.level_id, level_title: `#${course.level_id} (inactive)` }];
      }

      this.editForm = {
        courseId: course.course_id,
        title: course.title || '',
        description: course.description || '',
        courseLink: course.course_link || '',
        fee: Number(course.fee || 0),
        categoryId: course.category_id ?? null,
        levelId: course.level_id ?? null,
        startDate: moment(schedule.start || course.start_date).isValid()
          ? moment(schedule.start || course.start_date).format('YYYY-MM-DD') : '',
        endDate: moment(schedule.end || course.end_date).isValid()
          ? moment(schedule.end || course.end_date).format('YYYY-MM-DD') : '',
        weekdayIds: Array.isArray(schedule.weekdays) ? schedule.weekdays.map(d => d.id) : [],
        startHour: Math.floor((timings[0] || 0) / 100),
        startMinute: (timings[0] || 0) % 100,
        endHour: Math.floor((timings[1] || 0) / 100),
        endMinute: (timings[1] || 0) % 100,
        max: config.max ?? '',
        joiningAfterStart: config.joining_After_start !== false,
        joiningBeforeFee: config.joining_before_fee === true,
        contents: contents.map(c => ({ ...c })),
        originalSchedule: schedule,
        originalConfig: config,
      };
      this.showEditModal = true;
    },
    parseJsonField(value) {
      if (!value) return null;
      if (typeof value !== 'string') return value;
      try {
        return JSON.parse(value);
      } catch (e) {
        return null;
      }
    },
    addChapter() {
      this.editForm.contents.push({ id: Date.now(), title: '', description: '', completed: false });
    },
    removeChapter(index) {
      this.editForm.contents.splice(index, 1);
    },
    async saveCourseEdits() {
      if (!this.editForm.courseId) return;
      this.savingCourse = true;
      try {
        const schedule = {
          ...this.editForm.originalSchedule,
          start: moment(this.editForm.startDate).toISOString(),
          end: moment(this.editForm.endDate).toISOString(),
          weekdays: WEEKDAYS.filter(w => this.editForm.weekdayIds.includes(w.id)),
          timings: [
            Number(this.editForm.startHour) * 100 + Number(this.editForm.startMinute),
            Number(this.editForm.endHour) * 100 + Number(this.editForm.endMinute),
          ],
        };
        const config = {
          ...this.editForm.originalConfig,
          max: this.editForm.max,
          joining_After_start: this.editForm.joiningAfterStart,
          joining_before_fee: this.editForm.joiningBeforeFee,
        };

        const payload = {
          course_id: this.editForm.courseId,
          title: this.editForm.title,
          description: this.editForm.description,
          course_link: this.editForm.courseLink,
          fee: Number(this.editForm.fee || 0),
          category_id: this.editForm.categoryId,
          level_id: this.editForm.levelId,
          start_date: moment(this.editForm.startDate).format('YYYY-MM-DD HH:mm:ss'),
          end_date: moment(this.editForm.endDate).format('YYYY-MM-DD HH:mm:ss'),
          schedule: JSON.stringify(schedule),
          config: JSON.stringify(config),
          contents: JSON.stringify(this.editForm.contents),
        };

        await CourseService.updateCourse(payload);
        this.$bvToast.toast('Course updated successfully', {
          title: 'Success',
          variant: 'success',
          solid: true,
        });
        this.showEditModal = false;
        if (this.selectedCourse && this.selectedCourse.course_id === this.editForm.courseId) {
          this.showCourseModal = false;
        }
        this.loadCourses();
      } catch (error) {
        this.$bvToast.toast(error.response?.data?.message || 'Failed to update course', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      } finally {
        this.savingCourse = false;
      }
    },
    async approveCourse(course) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to approve "${course.title}"?`,
        {
          title: 'Confirm Approval',
          okVariant: 'success',
          okTitle: 'Approve',
          cancelTitle: 'Cancel',
        }
      );

      if (confirmed) {
        try {
          await CourseService.approveCourse(course.course_id);
          this.$bvToast.toast('Course approved successfully', {
            title: 'Success',
            variant: 'success',
            solid: true,
          });
          this.showCourseModal = false;
          this.loadCourses();
        } catch (error) {
          this.$bvToast.toast('Failed to approve course', {
            title: 'Error',
            variant: 'danger',
            solid: true,
          });
        }
      }
    },
    async rejectCourse(course) {
      this.$bvModal
        .msgBoxConfirm('Please provide a reason for rejecting this course:', {
          title: 'Reject Course',
          okVariant: 'danger',
          okTitle: 'Reject',
          cancelTitle: 'Cancel',
        })
        .then(async (confirmed) => {
          if (confirmed) {
            try {
              await CourseService.rejectCourse(course.course_id, 'Admin rejected');
              this.$bvToast.toast('Course rejected', {
                title: 'Success',
                variant: 'success',
                solid: true,
              });
              this.showCourseModal = false;
              this.loadCourses();
            } catch (error) {
              this.$bvToast.toast('Failed to reject course', {
                title: 'Error',
                variant: 'danger',
                solid: true,
              });
            }
          }
        });
    },
    async deleteCourse(course) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to delete "${course.title}"? This action cannot be undone.`,
        {
          title: 'Confirm Deletion',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
        }
      );

      if (confirmed) {
        try {
          await CourseService.deleteCourse(course.course_id);
          this.$bvToast.toast('Course deleted successfully', {
            title: 'Success',
            variant: 'success',
            solid: true,
          });
          this.loadCourses();
        } catch (error) {
          this.$bvToast.toast('Failed to delete course', {
            title: 'Error',
            variant: 'danger',
            solid: true,
          });
        }
      }
    },
    getStatusVariant(status) {
      const variants = {
        pending: 'warning',
        approved: 'success',
        rejected: 'danger',
        published: 'info',
      };
      return variants[status] || 'secondary';
    },
    formatDate(date) {
      return moment(date).format('MMM DD, YYYY');
    },
    formatTime(value) {
      // schedule.timings is encoded as HHMM (e.g. 1830 = 6:30 PM), the same
      // way the mobile app's AddCourse screen writes it -- NOT minutes-since-
      // midnight.
      if (!value && value !== 0) return 'N/A';
      const hours = Math.floor(value / 100);
      const mins = value % 100;
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours > 12 ? hours - 12 : (hours === 0 ? 12 : hours);
      return `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
    },
    getWeekdaysText(weekdays) {
      if (!weekdays || !Array.isArray(weekdays)) return 'N/A';
      return weekdays.map(day => day.title || day).join(', ');
    },
    truncate(text, length) {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + '...' : text;
    },
  },
};
</script>

<style scoped>
.feather-sm {
  width: 16px;
  height: 16px;
}
</style>
